import { queryOptions } from "@tanstack/react-query";
import { preserveConnectStepOnStaleGatewayRead } from "@/app/onboarding/lib/merge-onboarding-progress";
import { isOnboardingActive } from "@/app/onboarding/lib/selectors";
import { experienceApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import type { EligibilityResponse } from "./get-eligibility";
import { onboardingKeys } from "./query-keys";

type OnboardingJtbd = {
  id: string;
  name: string;
  description: string;
  tools: string[];
  llm_instructions: string;
  example_prompts: string[];
  services: string[];
};

export type OnboardingGateway = {
  id: string;
  name: string;
  slug: string;
  auth_type: "arcade" | "arcade_header" | "user_source";
  allowed_tools: string[];
  jtbd: OnboardingJtbd | null;
  services: string[];
};

export type OnboardingCurrentStep = "jtbd" | "connect" | "done" | null;

export type OnboardingProgress = {
  account_has_executed_tool_externally: boolean;
  current_step: OnboardingCurrentStep;
  gateway: OnboardingGateway | null;
};

export type OnboardingProgressResponse = OnboardingProgress | null;

const fetchOnboardingProgress = async (
  orgId?: string,
  projectId?: string
): Promise<OnboardingProgressResponse> => {
  const res = await experienceApi.get<OnboardingProgressResponse>(
    "/api/onboarding/progress",
    { params: { orgId, projectId } }
  );
  return res.data ?? null;
};

export type OnboardingProgressQueryOptions = {
  /**
   * When true, keep the query fresh while onboarding is active: refetch every
   * 15s and on window focus while onboarding is active. Live onboarding
   * surfaces opt in so step-completion and tool-call activation appear without
   * a reload; prefetch-only callers leave it off to avoid background polling.
   */
  poll?: boolean;
};

function getCachedEligibility(
  orgId?: string,
  projectId?: string
): EligibilityResponse | undefined {
  return queryClient.getQueryData<EligibilityResponse>(
    onboardingKeys.eligibility({
      scope: { organizationId: orgId, projectId },
    })
  );
}

export const onboardingProgressQueryOptions = (
  orgId?: string,
  projectId?: string,
  options?: OnboardingProgressQueryOptions
) => {
  const progressKey = onboardingKeys.progress({
    scope: { organizationId: orgId, projectId },
  });

  return queryOptions<OnboardingProgressResponse>({
    queryKey: progressKey,
    queryFn: async () => {
      const previous =
        queryClient.getQueryData<OnboardingProgressResponse>(progressKey);
      const incoming = await fetchOnboardingProgress(orgId, projectId);
      return preserveConnectStepOnStaleGatewayRead(previous, incoming);
    },
    refetchInterval: (query) => {
      if (!options?.poll) {
        return false;
      }
      const progress = query.state.data;
      if (progress == null) {
        return false;
      }
      // isOnboardingActive is `active && !dismissed`, so this stops polling
      // both for ineligible users and once the user dismisses onboarding.
      if (!isOnboardingActive(getCachedEligibility(orgId, projectId))) {
        return false;
      }
      return 15_000;
    },
    refetchOnWindowFocus: (query) => {
      if (!options?.poll) {
        return false;
      }
      const progress = query.state.data;
      if (progress == null) {
        return false;
      }
      return isOnboardingActive(getCachedEligibility(orgId, projectId));
    },
  });
};
